import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { message } from 'antd';
import { useAuthStore } from '../stores/auth.store';
import type { IApiResponse } from '@talent-week/shared-types';

/**
 * Axios 请求封装
 *
 * 功能：
 * - 统一 baseURL 配置
 * - 请求拦截器：自动注入 JWT Token
 * - 响应拦截器：统一错误处理、Token 过期跳转登录
 * - 统一响应格式解包（提取 data 字段）
 */

/** 后端 API 基础地址 */
const BASE_URL = '/api';

/** 请求超时时间（毫秒） */
const TIMEOUT = 30000;

/** 创建 Axios 实例 */
const request: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 请求拦截器
 * 自动注入 Authorization Bearer Token
 */
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  },
);

/**
 * 响应拦截器
 * 统一处理响应格式和错误
 */
request.interceptors.response.use(
  (response: AxiosResponse<IApiResponse>) => {
    const { data } = response;

    // 如果响应不符合统一格式，直接返回
    if (!data || typeof data !== 'object' || !('code' in data)) {
      return response;
    }

    // 业务成功
    if (data.code === 200) {
      return response;
    }

    // 业务失败
    const errorMsg = data.message || '请求失败';
    message.error(errorMsg);
    return Promise.reject(new Error(errorMsg));
  },
  (error: any) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          message.error('登录已过期，请重新登录');
          useAuthStore.getState().logout();
          // 跳转登录页
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;

        case 403:
          message.error(data?.message || '权限不足');
          break;

        case 404:
          message.error('请求的资源不存在');
          break;

        case 429:
          message.error('请求过于频繁，请稍后再试');
          break;

        case 500:
          message.error(data?.message || '服务器内部错误');
          break;

        default:
          message.error(data?.message || `请求失败 (${status})`);
      }
    } else if (error.code === 'ECONNABORTED') {
      message.error('请求超时，请检查网络后重试');
    } else if (error.message === 'Network Error') {
      message.error('网络连接异常，请检查网络');
    } else {
      message.error(error.message || '未知错误');
    }

    return Promise.reject(error);
  },
);

/**
 * 封装 GET 请求
 */
export async function get<T = any>(
  url: string,
  params?: Record<string, any>,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await request.get<IApiResponse<T>>(url, {
    params,
    ...config,
  });
  return response.data.data;
}

/**
 * 封装 POST 请求
 */
export async function post<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await request.post<IApiResponse<T>>(url, data, config);
  return response.data.data;
}

/**
 * 封装 PUT 请求
 */
export async function put<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await request.put<IApiResponse<T>>(url, data, config);
  return response.data.data;
}

/**
 * 封装 PATCH 请求
 */
export async function patch<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await request.patch<IApiResponse<T>>(url, data, config);
  return response.data.data;
}

/**
 * 封装 DELETE 请求
 * data 为可选请求体（如批量删除接口需要携带 seatIds），通过 config.data 透传
 */
export async function del<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await request.delete<IApiResponse<T>>(url, {
    data,
    ...config,
  });
  return response.data.data;
}

/**
 * 封装文件上传
 */
export async function upload<T = any>(
  url: string,
  file: File,
  folder?: string,
  onProgress?: (percent: number) => void,
): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await request.post<IApiResponse<T>>(url, formData, {
    params: { folder },
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percent);
      }
    },
  });

  return response.data.data;
}

export default request;
